// GuildProfile.tsx
// The public face of a guild: what a recruit (or a member) sees when they
// open it from the directory. Same look as the directory and the Alignment
// form: Inter, glass panels, real game art.
//
// Everything is PER GAME: a guild that plays Anniversary and WoW Forever has
// a realm, faction, schedule, roster and needs in each, and the game tabs
// switch all of it. Realm/faction/region come from the stats payload
// (guild_game), never from the guild row's legacy mirror, which points at
// just one of the games.
//
// Numbers are measured from the guild's raids here (auth/lib/guild_stats.js).
// With too little history they are marked as such, and the schedule falls
// back to what officers wrote ("as stated").
import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { DateTime } from "luxon";
import { BODY, DISPLAY, Glass, Icon, ROLE_COLOR, tc, useAlignmentTheme } from "./alignmentUi";
import { useTimezone } from "./lib/TimezoneContext";
import { shortGameName } from "./lib/gameName";
import { formatSchedule, StylePill } from "./GuildDirectory";
import type { GuildGameStats } from "./GuildDirectory";

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL || "";

export type ProfileGuild = {
  id: string; name: string; tag?: string | null; logoUrl?: string | null;
  aboutRaw?: string | null; contactInfo?: string | null; scheduleSummary?: string | null;
  recruiting?: boolean; memberCount?: number;
};

function factionColor(f: string | null | undefined): string | null {
  const x = (f || "").toLowerCase();
  if (x.includes("alliance")) return "#5AA8FF";
  if (x.includes("horde")) return "#FF5A4E";
  return null;
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">{children}</div>;
}

function Tile({ label, value, sub, loading }: { label: string; value: React.ReactNode; sub?: React.ReactNode; loading?: boolean }) {
  return (
    <Glass className="p-4">
      <Eyebrow>{label}</Eyebrow>
      {loading ? (
        <div className="mt-2 h-6 w-24 animate-pulse rounded bg-[var(--c-sunken-2)]" />
      ) : (
        <>
          <div className="mt-1.5 text-[20px] leading-tight text-[var(--c-ink)]" style={DISPLAY}>{value}</div>
          {sub && <div className="mt-1 text-[12px] text-[var(--c-ink-3)]">{sub}</div>}
        </>
      )}
    </Glass>
  );
}

/** Tank / healer / DPS as three coverage bars, like the Alignment role panel. */
function RoleBars({ mix }: { mix: { tank: number; healer: number; dps: number } }) {
  const rows = [
    { key: "tank", label: "Tanks", n: mix.tank, color: ROLE_COLOR.tank },
    { key: "healer", label: "Healers", n: mix.healer, color: ROLE_COLOR.healer },
    { key: "dps", label: "DPS", n: mix.dps, color: ROLE_COLOR.melee_dps },
  ];
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-3">
          <span className="w-16 text-[13px] font-semibold" style={{ color: tc(r.color) }}>{r.label}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--c-sunken-2)]">
            <div className="h-full rounded-full" style={{ width: `${(r.n / max) * 100}%`, background: r.color }} />
          </div>
          <span className="w-6 text-right text-[13px] font-bold text-[var(--c-ink)]">{r.n}</span>
        </div>
      ))}
    </div>
  );
}

export default function GuildProfile({
  guild, joinPanel,
}: {
  guild: ProfileGuild;
  /** The Request to Join panel, when this viewer can apply. */
  joinPanel?: React.ReactNode;
}) {
  useAlignmentTheme();
  const tz = useTimezone();
  const [games, setGames] = useState<GuildGameStats[] | null>(null);
  const [gameId, setGameId] = useState("");

  useEffect(() => {
    let cancelled = false;
    setGames(null);
    fetch(`/guilds/${encodeURIComponent(guild.id)}/stats`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        const list: GuildGameStats[] = d && Array.isArray(d.games) ? d.games : [];
        setGames(list);
        // Start on the game with the most recent raid.
        const first = [...list].sort((a, b) => (b.history.lastRaidAt || "").localeCompare(a.history.lastRaidAt || ""))[0];
        setGameId(first ? first.gameId : "");
      })
      .catch(() => { if (!cancelled) setGames([]); });
    return () => { cancelled = true; };
  }, [guild.id]);

  const game = useMemo(() => (games || []).find((g) => g.gameId === gameId) || null, [games, gameId]);
  const loading = games === null;
  const logo = guild.logoUrl ? (guild.logoUrl.startsWith("http") ? guild.logoUrl : `${AUTH_BASE_URL}${guild.logoUrl}`) : null;
  const fc = factionColor(game?.faction);

  const measured = game && game.schedule.length ? formatSchedule(game.schedule, tz) : null;
  const stated = (guild.scheduleSummary || "").trim();
  const rostered = !!game && game.roster.avgSeated >= 5; // raids actually carry rosters here
  const since = game?.history.firstRaidAt ? DateTime.fromISO(game.history.firstRaidAt).setZone(tz) : null;
  const about = guild.aboutRaw ? DOMPurify.sanitize(guild.aboutRaw) : "";

  return (
    <div style={BODY} className="space-y-6">
      {/* HERO: the guild's own art, kept dark whatever the palette. */}
      <div className="lfm-dark relative overflow-hidden rounded-3xl border border-[var(--c-line)] bg-black shadow-[var(--c-shadow-lg)]">
        {logo ? (
          <img src={logo} alt="" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-45 blur-[2px]" />
        ) : (
          <div className="char-modal-splash-idle absolute inset-0" />
        )}
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(to top, rgba(0,0,0,0.92), rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.25))${fc ? `, radial-gradient(90% 120% at 100% 0%, ${fc}40, transparent 60%)` : ""}` }}
        />
        <div className="relative flex flex-col gap-5 p-6 sm:flex-row sm:items-end sm:p-8">
          <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/15 bg-black/60 shadow-2xl">
            {logo ? <img src={logo} alt={`${guild.name} logo`} className="h-full w-full object-cover" /> : <span className="text-4xl font-bold text-white/60">{guild.name.charAt(0)}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {guild.recruiting ? (
                <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/90 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" /> Recruiting
                </span>
              ) : (
                <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white/70">Not recruiting</span>
              )}
              {game && <span className="rounded-md bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white">{shortGameName(game.gameName)}</span>}
            </div>
            <h1 className="mt-2 flex flex-wrap items-baseline gap-x-3 text-4xl leading-tight text-white sm:text-5xl" style={DISPLAY}>
              {guild.name}
              {guild.tag && <span className="font-mono text-base font-normal text-white/50">[{guild.tag}]</span>}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-2 text-[14px] text-white/75">
              {game?.region && <span className="font-semibold text-white">{game.region}</span>}
              {game?.serverName && <span>· {game.serverName}</span>}
              {fc && game?.faction && (
                <span className="flex items-center gap-1.5">· <span className="h-2 w-2 rounded-full" style={{ background: fc }} />{game.faction}</span>
              )}
            </div>
            {game && game.styles.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {game.styles.map((st) => <StylePill key={st.id} style={st} />)}
              </div>
            )}
          </div>
          {games && games.length > 1 && (
            <div className="inline-flex self-start rounded-xl border border-white/15 bg-black/40 p-1 sm:self-end">
              {games.map((g) => (
                <button
                  key={g.gameId}
                  type="button"
                  onClick={() => setGameId(g.gameId)}
                  className={`rounded-lg px-3 py-1.5 text-[13px] font-semibold transition ${g.gameId === gameId ? "bg-white/20 text-white" : "text-white/60 hover:text-white"}`}
                >
                  {shortGameName(g.gameName)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AT A GLANCE */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Raid nights"
          loading={loading}
          value={measured ? measured.text : stated ? <span className="text-[16px]">{stated}</span> : "Not set"}
          sub={measured
            ? <>{measured.hours ? `${measured.hours}h · ` : ""}{game!.history.verified ? <span className="font-semibold text-emerald-700 dark:text-emerald-400">✓ measured, in your time</span> : "from recent raids, in your time"}</>
            : stated ? "as stated by the guild" : "no raids on Rallify yet"}
        />
        <Tile
          label="Raids per week"
          loading={loading}
          value={game && game.history.raidsTotal ? game.raidsPerWeek : "·"}
          sub={game && game.history.raidsTotal ? `raided ${game.history.raidingWeeks} of the last ${game.history.windowWeeks} weeks` : "no raids yet"}
        />
        <Tile
          label="Roster"
          loading={loading}
          value={rostered ? `${game!.roster.activeRaiders} raiding` : `${game?.members.players || guild.memberCount || 0} players`}
          sub={rostered ? `of ${game!.members.players} players · about ${game!.roster.avgSeated} per raid` : "players with a character here"}
        />
        <Tile
          label="On Rallify since"
          loading={loading}
          value={since ? since.toFormat("LLL yyyy") : "·"}
          sub={game && game.history.raidsTotal ? `${game.history.raidsTotal} raid${game.history.raidsTotal === 1 ? "" : "s"} run` : "first raid still to come"}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
          <Glass className="p-6">
            <h2 className="text-xl text-[var(--c-ink)]" style={DISPLAY}>About</h2>
            {about ? (
              <div className="prose prose-sm mt-3 max-w-none text-[var(--c-ink)] dark:prose-invert" dangerouslySetInnerHTML={{ __html: about }} />
            ) : (
              <p className="mt-2 text-[14px] text-[var(--c-ink-4)]">No description yet.</p>
            )}
            {guild.contactInfo && guild.contactInfo.trim() && (
              <div className="mt-4 rounded-2xl bg-[var(--c-sunken)] px-4 py-3">
                <Eyebrow>Contact</Eyebrow>
                <div className="mt-1 whitespace-pre-line text-[14px] text-[var(--c-ink-2)]">{guild.contactInfo}</div>
              </div>
            )}
          </Glass>

          {game && game.content.length > 0 && (
            <Glass className="p-6">
              <div className="flex items-baseline justify-between">
                <h2 className="text-xl text-[var(--c-ink)]" style={DISPLAY}>Currently raiding</h2>
                <span className="text-[12px] text-[var(--c-ink-4)]">last 6 weeks</span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {game.content.map((c) => (
                  <div key={c.key} className="lfm-dark relative h-24 overflow-hidden rounded-2xl bg-black">
                    {c.image && <img src={c.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <div className="text-[15px] font-bold text-white">{c.label}{c.size ? <span className="ml-1.5 text-[12px] font-semibold text-white/60">{c.size}</span> : null}</div>
                      <div className="text-[12px] text-white/70">
                        {c.raids} raid{c.raids === 1 ? "" : "s"} · last {DateTime.fromISO(c.lastAt).setZone(tz).toFormat("d LLL")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Glass>
          )}

          {rostered && (
            <Glass className="p-6">
              <div className="flex items-baseline justify-between">
                <h2 className="text-xl text-[var(--c-ink)]" style={DISPLAY}>Roster make-up</h2>
                <span className="text-[12px] text-[var(--c-ink-4)]">raiders seen in the last 4 weeks</span>
              </div>
              <div className="mt-4"><RoleBars mix={game!.roster.roleMix} /></div>
            </Glass>
          )}
        </div>

        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <Glass className="p-5">
            <Eyebrow>Looking for</Eyebrow>
            {!guild.recruiting ? (
              <p className="mt-2 text-[14px] text-[var(--c-ink-3)]">Not recruiting right now.</p>
            ) : game && game.needs.length ? (
              <div className="mt-3 space-y-2">
                {game.needs.map((n) => (
                  <div key={n.specId} className="flex items-center gap-2.5">
                    <Icon src={n.image} alt={n.label} size={30} ring={n.priority === "high" ? "#f59e0b" : null} />
                    <span className="flex-1 text-[14px] font-semibold" style={{ color: tc(n.classColor || undefined) }}>{n.label} {n.className}</span>
                    {n.priority === "high" && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-700 dark:text-amber-300">High</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-[14px] font-semibold text-[var(--c-ink-2)]">Open to every role.</p>
            )}
          </Glass>

          {joinPanel}

          {game?.logsUrl && (
            <a
              href={game.logsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3 text-[14px] font-semibold text-[var(--c-ink)] shadow-[var(--c-shadow-sm)] transition hover:border-[var(--c-line-strong)]"
            >
              {game.logsSite?.name || "Logs"} guild page
              <span className="text-[var(--c-ink-4)]">↗</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
